import { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import api from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Search, Plus, AlertCircle, Check, X } from 'lucide-react';
import { toast } from 'sonner';

const TRADE_CATEGORIES = [
  'Carpenter', 'Electrician', 'Plumber', 'Painter', 'Mason', 'Tile Layer',
  'Glass / Mirror', 'False Ceiling', 'POP / Putty', 'HVAC', 'Furniture',
  'Material Supplier', 'Other'
];

const norm = (s) => (s || '').trim().toLowerCase();

function isPossibleDuplicate(typed, existingName) {
  const a = norm(typed); const b = norm(existingName);
  if (!a || !b) return false;
  if (a === b) return true;
  if (a.length >= 4 && b.length >= 4 && (a.includes(b) || b.includes(a))) return true;
  return false;
}

export default function VendorSelect({ value, onChange, placeholder = 'Search or select vendor', testId = 'vendor-select', autoFocus = false }) {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [open, setOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ name: '', phone: '', category: '', notes: '' });
  const [duplicate, setDuplicate] = useState(null);
  const [pos, setPos] = useState(null);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  // Compute portal position whenever dropdown opens or window scrolls/resizes
  useEffect(() => {
    if (!open) { setPos(null); return; }
    const update = () => {
      if (!inputRef.current) return;
      const r = inputRef.current.getBoundingClientRect();
      setPos({ top: r.bottom + 4, left: r.left, width: r.width });
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [open]);

  // Load vendor list once on mount and cache locally
  useEffect(() => {
    let mounted = true;
    setLoading(true);
    api.get('/agencies')
      .then(res => { if (mounted) setVendors(Array.isArray(res.data) ? res.data : []); })
      .catch(() => {})
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);

  // Debounce typing for filter
  useEffect(() => {
    const t = setTimeout(() => setDebounced(query), 200);
    return () => clearTimeout(t);
  }, [query]);

  // Click-outside to close dropdown
  useEffect(() => {
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  useEffect(() => {
    if (autoFocus && inputRef.current) inputRef.current.focus();
  }, [autoFocus]);

  const filtered = useMemo(() => {
    const q = norm(debounced);
    if (!q) return vendors;
    return vendors.filter(v => norm(v.name).includes(q));
  }, [vendors, debounced]);

  const exactMatch = useMemo(
    () => vendors.some(v => norm(v.name) === norm(query)),
    [vendors, query]
  );

  const selectVendor = (v) => {
    onChange(v.name, v);
    setQuery('');
    setOpen(false);
  };

  const clearSelection = (e) => {
    e.stopPropagation();
    onChange('');
    setQuery('');
    setOpen(false);
  };

  const openCreate = () => {
    const dup = vendors.find(v => isPossibleDuplicate(query, v.name));
    setDuplicate(dup || null);
    setCreateForm({ name: query.trim(), phone: '', category: '', notes: '' });
    setCreateOpen(true);
    setOpen(false);
  };

  const saveVendor = async () => {
    if (!createForm.name.trim()) { toast.error('Vendor name is required'); return; }
    try {
      const res = await api.post('/agencies', createForm);
      const newV = res.data;
      setVendors(prev => [...prev, newV]);
      onChange(newV.name, newV);
      toast.success('Vendor created and selected');
      setCreateOpen(false);
      setCreateForm({ name: '', phone: '', category: '', notes: '' });
      setDuplicate(null);
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Failed to create vendor';
      toast.error(msg);
    }
  };

  const useExisting = () => {
    if (!duplicate) return;
    onChange(duplicate.name, duplicate);
    setCreateOpen(false);
    setDuplicate(null);
    toast.success(`Selected existing vendor "${duplicate.name}"`);
  };

  return (
    <>
      <div ref={wrapRef} className="relative" data-testid={testId}>
        <div className="relative">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7280] pointer-events-none" />
          <input
            ref={inputRef}
            type="text"
            value={open ? query : (value || '')}
            onChange={e => { setQuery(e.target.value); setOpen(true); }}
            onFocus={() => { setQuery(''); setOpen(true); }}
            placeholder={value ? value : placeholder}
            className="w-full h-10 pl-9 pr-9 rounded-xl border border-[#D4D0C8] bg-white text-sm focus:outline-none focus:border-[#3A8C8C] transition-colors"
            data-testid={`${testId}-input`}
            autoComplete="off"
          />
          {value && !open && (
            <button
              type="button"
              onClick={clearSelection}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-[#E8E6E1] transition-colors"
              data-testid={`${testId}-clear`}
              aria-label="Clear vendor"
            >
              <X size={11} className="text-[#6B7280]" />
            </button>
          )}
        </div>

        {open && pos && createPortal(
          <div
            className="fixed bg-white border border-[#E8E6E1] rounded-xl shadow-lg max-h-64 overflow-auto"
            style={{ top: pos.top, left: pos.left, width: pos.width, zIndex: 9999, pointerEvents: 'auto' }}
            data-testid={`${testId}-dropdown`}
            onMouseDown={(e) => e.stopPropagation()}
          >
            {loading && <div className="px-3 py-3 text-xs text-[#6B7280]">Loading vendors…</div>}

            {!loading && !query.trim() && filtered.length === 0 && (
              <div className="px-3 py-3 text-xs text-[#6B7280]">Start typing to search vendors</div>
            )}

            {!loading && query.trim() && filtered.length === 0 && (
              <div className="px-3 py-3 text-xs text-[#6B7280]">No vendors found</div>
            )}

            {!loading && filtered.slice(0, 50).map(v => (
              <button
                key={v.id}
                type="button"
                onClick={() => selectVendor(v)}
                className="w-full text-left px-3 py-2 hover:bg-[#FAFAF8] flex items-center gap-2 transition-colors"
                data-testid={`${testId}-option-${v.id}`}
              >
                {value === v.name
                  ? <Check size={12} className="text-[#3A8C8C] shrink-0" />
                  : <span className="w-3 shrink-0" />}
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-[#2C2C2E] truncate">{v.name}</p>
                  {(v.category || v.phone) && (
                    <p className="text-[10px] text-[#6B7280] truncate">
                      {v.category || ''}{v.category && v.phone ? ' · ' : ''}{v.phone || ''}
                    </p>
                  )}
                </div>
              </button>
            ))}

            {query.trim() && !exactMatch && (
              <button
                type="button"
                onClick={openCreate}
                className="w-full text-left px-3 py-2 border-t border-[#E8E6E1] bg-[#3A8C8C]/5 hover:bg-[#3A8C8C]/10 flex items-center gap-2 transition-colors"
                data-testid={`${testId}-add-new`}
              >
                <Plus size={12} className="text-[#3A8C8C] shrink-0" />
                <span className="text-xs text-[#3A8C8C] font-medium truncate">
                  Add &ldquo;<span className="font-semibold">{query}</span>&rdquo; as new vendor
                </span>
              </button>
            )}
          </div>,
          document.body
        )}
      </div>

      <Dialog open={createOpen} onOpenChange={(o) => { setCreateOpen(o); if (!o) setDuplicate(null); }}>
        <DialogContent className="max-w-md" data-testid="vendor-create-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">Create New Vendor</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            {duplicate && (
              <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg bg-[#C67D5B]/10 border border-[#C67D5B]/30" data-testid="vendor-duplicate-warning">
                <AlertCircle size={14} className="text-[#C67D5B] mt-0.5 shrink-0" />
                <div className="flex-1">
                  <p className="text-[11px] font-semibold text-[#C67D5B]">Possible duplicate</p>
                  <p className="text-[10px] text-[#6B7280] mt-0.5">
                    &ldquo;{duplicate.name}&rdquo; already exists{duplicate.category ? ` (${duplicate.category})` : ''}.
                  </p>
                  <button
                    type="button"
                    onClick={useExisting}
                    className="text-[10px] text-[#3A8C8C] underline mt-1 hover:text-[#2E7A7A]"
                    data-testid="vendor-use-existing"
                  >
                    Use existing instead
                  </button>
                </div>
              </div>
            )}
            <div>
              <Label className="text-xs text-[#6B7280] uppercase">Vendor Name *</Label>
              <Input
                value={createForm.name}
                onChange={e => setCreateForm({ ...createForm, name: e.target.value })}
                className="mt-1 h-10 rounded-xl border-[#D4D0C8]"
                placeholder="e.g. Stone World"
                data-testid="vendor-create-name"
                autoFocus
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-[#6B7280] uppercase">Phone</Label>
                <Input
                  value={createForm.phone}
                  onChange={e => setCreateForm({ ...createForm, phone: e.target.value })}
                  className="mt-1 h-10 rounded-xl border-[#D4D0C8]"
                  placeholder="+91 …"
                  data-testid="vendor-create-phone"
                />
              </div>
              <div>
                <Label className="text-xs text-[#6B7280] uppercase">Trade / Category</Label>
                <select
                  value={createForm.category}
                  onChange={e => setCreateForm({ ...createForm, category: e.target.value })}
                  className="mt-1 h-10 w-full px-3 rounded-xl border border-[#D4D0C8] bg-white text-sm focus:outline-none focus:border-[#3A8C8C]"
                  data-testid="vendor-create-category"
                >
                  <option value="">Select…</option>
                  {TRADE_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>
            <div>
              <Label className="text-xs text-[#6B7280] uppercase">Notes</Label>
              <textarea
                value={createForm.notes}
                onChange={e => setCreateForm({ ...createForm, notes: e.target.value })}
                className="mt-1 w-full h-16 px-4 py-2 bg-white border border-[#D4D0C8] rounded-xl text-sm resize-none focus:outline-none focus:border-[#3A8C8C]"
                data-testid="vendor-create-notes"
              />
            </div>
            <div className="flex gap-2 pt-2">
              <button
                type="button"
                onClick={() => { setCreateOpen(false); setDuplicate(null); }}
                className="flex-1 h-10 rounded-full border border-[#D4D0C8] text-sm font-medium text-[#6B7280] hover:bg-[#E8E6E1] transition-colors"
                data-testid="vendor-create-cancel"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={saveVendor}
                className="flex-1 h-10 rounded-full bg-[#3A8C8C] text-white text-sm font-medium hover:bg-[#2E7A7A] transition-colors"
                data-testid="vendor-create-save"
              >
                Save &amp; Select
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
